<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  visible: boolean;
  confirmType: string;
  entityDisplayName: string;
  entityName: string;
  hasUnsavedFormInput?: boolean;
  saveLoading?: boolean;
  deleteLoading?: boolean;
  actions?: string[]; // Passed from MenuButton
}

const props = withDefaults(defineProps<Props>(), {
  hasUnsavedFormInput: false,
  saveLoading: false,
  deleteLoading: false,
  actions: () => []
});

const emit = defineEmits<{
  'update:visible': [visible: boolean];
  'save': [];
  'delete': [];
}>();
// i18n Setup
const { t } = useI18n();

const availableActions = computed<string[]>(() => props.actions ?? []);

//Actions name changes depending on the selected language and are compared to configure the message of the dialog
const deleteAction = t('common.actions.delete');

const isCriticalAction = computed(() =>
  availableActions.value.includes(props.confirmType) && props.confirmType !== deleteAction
);

const isDeleteConfirmation = computed(() =>
  isCriticalAction.value ||
  props.confirmType === 'delete' ||
  props.confirmType === 'deleteConfig' ||
  props.confirmType === deleteAction
);

// Compute the correct message key based on props
const confirmMessageKey = computed(() => {
  if (props.hasUnsavedFormInput) {
    if (isDeleteConfirmation.value) {
      return t(`common.dialogs.delete.confirm`);
    }
    return 'common.dialogs.safeForm.unsavedChanges';
  }
  //Dialog for critic actions
  if (isCriticalAction.value) {
    return t('common.dialogs.criticalActions.confirm', { criticalAction: props.confirmType });
  }

  const actionType = isDeleteConfirmation.value ? 'Delete' : 'Save';
  const hasEntityName = !!props.entityName;
  return hasEntityName
    ? `common.dialogs.safeForm.confirmAction${actionType}WithEntity`
    : `common.dialogs.safeForm.confirmAction${actionType}`;
});

// Local state for the dialog visibility
const dialogVisible = ref(props.visible);

// Synchronize local state with the prop
watch(() => props.visible, (newValue) => {
  dialogVisible.value = newValue;
});

// Synchronize changes back to the parent
watch(dialogVisible, (newValue) => {
  if (newValue !== props.visible) {
    emit('update:visible', newValue);
  }
});

function close() {
  dialogVisible.value = false;
}

function handleSave() {
  emit('save');
  dialogVisible.value = false;
}

function handleDelete() {
  emit('delete');
  dialogVisible.value = false;
}
</script>

<template>
  <Dialog
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :header="t('common.dialogs.confirmation')"
    @hide="close"
    @keydown.enter.prevent="isDeleteConfirmation ? handleDelete() : handleSave()"
  >
    <span class="whitespace-pre-line">
      {{ t(confirmMessageKey, { entityName, entityDisplayName }) }}
    </span>
    <template #footer>
      <Button
        :severity="isDeleteConfirmation ? 'danger' : 'success'"
        :label="isCriticalAction ? confirmType : t(isDeleteConfirmation ? 'common.actions.delete' : 'common.actions.save')"
        @click="isDeleteConfirmation ? handleDelete() : handleSave()"
        :loading="isDeleteConfirmation ? props.deleteLoading || false : props.saveLoading || false"
        autofocus
      />
      <Button
        severity="tertiary"
        :label="t('common.actions.cancel')"
        @click="close"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.whitespace-pre-line {
  white-space: pre-line;
}
</style>